/**
 * Sharing one Moon Exchange listing.
 *
 * A listing is posted to be FOUND, so it shares through the same bar the
 * storybook uses -- the native sheet where the device has one (which is how a
 * phone reaches WhatsApp), web intents where it does not, and copy everywhere.
 * Building a second, weaker share path for this screen would have given the
 * exchange the one thing the other surfaces already solved.
 *
 * What it deliberately does NOT carry is the seller. The exchange screen is
 * behind a login and shows who is selling; the link is public and reaches
 * whoever it is forwarded to. The picture, the tags and this text all stop at
 * the card, its price, and how many are left.
 */

import { i18n } from '../i18n.js';
import { DiscordLinks } from './discord-links.js';

const MODAL_CLASS = 'share-modal';

/*
 * THE FALLBACK MUST BE INTERPOLATED TOO.
 *
 * i18n resolves late, and until it does every `uiText` call falls through to
 * the English default. Returning that default verbatim printed a literal
 * "{{price}} Stardust" on screen -- the placeholder is only a placeholder if
 * something substitutes it, and the resolved path did while this one did not.
 */
function uiText(key, fallback, vars) {
  try {
    const value = i18n?.t?.(key, vars);
    if (typeof value === 'string' && value && value !== key) return value;
  } catch (_) { /* copy must never break a share */ }
  const text = typeof fallback === 'string' ? fallback : key;
  if (!vars) return text;
  return text.replace(/\{\{(\w+)\}\}/g, (whole, name) => (
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : whole
  ));
}

/**
 * The URL a player sends. `/play?listing=ID` is both the page a visitor lands
 * on and the page the server hangs the Open Graph tags off, so one link serves
 * the human and the crawler.
 */
export function buildListingShareUrl(listing, origin) {
  const base = String(origin || window.location?.origin || '').replace(/\/+$/, '');
  return `${base}/play?listing=${encodeURIComponent(String(listing?.id || ''))}`;
}

/**
 * The line every network carries ahead of the link: the card and its price,
 * which the public page and its unfurl show as well. The price is a positive
 * whole number on every listing the server makes; one that somehow arrives
 * without it still says what is for sale.
 */
function buildListingShareText(listing) {
  const card = String(listing?.cardName || '');
  const price = Number(listing?.unitPrice);
  return Number.isSafeInteger(price) && price > 0
    ? uiText('exchange.share.text', "{{card}} is for sale on Moonfall's Moon Exchange for {{price}} Stardust.", {
      card,
      price: String(price),
    })
    : uiText('exchange.share.textUnpriced', "{{card}} is for sale on Moonfall's Moon Exchange.", { card });
}

let openSheet = null;

export function closeListingShare() {
  if (!openSheet) return;
  try { document.removeEventListener('keydown', openSheet.onKey, true); } catch (_) { /* noop */ }
  try { openSheet.el.parentNode.removeChild(openSheet.el); } catch (_) { /* noop */ }
  openSheet = null;
}

export function openListingShare(listing) {
  /* Never two sheets: a second click on a row behind the first one used to be
     enough, and the stacked backdrops trap the pointer. */
  closeListingShare();
  if (!listing?.id) return null;

  const url = buildListingShareUrl(listing);
  const el = document.createElement('div');
  el.className = MODAL_CLASS;
  el.id = 'exchange-share-modal';
  el.addEventListener('click', (event) => {
    if (event.target === el) closeListingShare();
  });

  const card = document.createElement('div');
  card.className = 'share-modal-card';

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'share-modal-close';
  close.setAttribute('aria-label', uiText('manga.nav.close', 'Close'));
  close.textContent = '×';
  close.addEventListener('click', closeListingShare);
  card.appendChild(close);

  const title = document.createElement('h2');
  title.className = 'share-modal-title';
  title.textContent = uiText('exchange.share.button', 'Share listing');
  card.appendChild(title);

  const subtitle = document.createElement('p');
  subtitle.className = 'share-modal-subtitle';
  subtitle.textContent = String(listing.cardName || '');
  card.appendChild(subtitle);

  /* The link itself, always present. It is the fallback when the share bundle
     has not loaded, and on desktop it is the thing most people actually use. */
  const field = document.createElement('input');
  field.className = 'share-modal-url';
  field.readOnly = true;
  field.value = url;
  field.addEventListener('focus', () => field.select());
  card.appendChild(field);

  const privacy = document.createElement('p');
  privacy.className = 'share-modal-privacy';
  privacy.textContent = uiText(
    'exchange.share.privacy',
    'Anyone with this link can see the card and its price.',
  );
  card.appendChild(privacy);

  const buttons = document.createElement('div');
  buttons.className = 'share-modal-buttons';
  card.appendChild(buttons);

  /* What a press made true ("copied", or that it could not be), on a line
     held from the first frame so its words never move the sheet. */
  const notice = document.createElement('p');
  notice.className = 'share-modal-toast';
  notice.setAttribute('role', 'status');
  notice.setAttribute('aria-live', 'polite');
  card.appendChild(notice);
  let noticeTimer = null;
  const say = (message) => {
    if (!message || !notice.isConnected) return;
    clearTimeout(noticeTimer);
    notice.textContent = String(message);
    notice.classList.add('share-modal-toast--show');
    noticeTimer = setTimeout(() => notice.classList.remove('share-modal-toast--show'), 4500);
  };

  el.appendChild(card);
  document.body.appendChild(el);

  const share = window.MoonfallStorybookShare;
  if (typeof share?.attachShareButtons === 'function') {
    share.attachShareButtons(buttons, {
      url,
      title: String(listing.cardName || ''),
      text: buildListingShareText(listing),
      /* Its own funnel. Filing these under `storybook_share_*` would mix two
         surfaces that are read separately. The UTM tags on the LINK need the
         same thing and did not get it: every listing shared before this carried
         `utm_campaign=storybook_share&utm_content=story_link`, so the exchange's
         inbound traffic was indistinguishable from the storybook's. */
      analyticsPrefix: 'exchange',
      utmCampaign: 'exchange_share',
      utmContent: 'listing_link',
      surface: 'moon_exchange',
      language: i18n?.getLanguage?.() || 'en',
      onCopy: say,
      /* #trades, through its own counted invite (ui/discord-links.js). The
         bar shows it with the networks on a desk and beside the one Share on a
         phone, whose own sheet lists Discord only if its app is installed. */
      discord: DiscordLinks.inviteFor('trades') ? {
        invite: DiscordLinks.inviteFor('trades'),
        label: uiText('exchange.share.discord', 'Post it in #trades on Discord'),
        copied: uiText('exchange.share.discordCopied', 'Listing link copied — paste it in #trades'),
        copyFailed: uiText('exchange.share.discordCopyFailed', "Couldn't copy — copy the link above for #trades"),
        onOpened: () => DiscordLinks.noteOpened('trades'),
      } : null,
    });
  }

  const onKey = (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      closeListingShare();
    }
  };
  document.addEventListener('keydown', onKey, true);
  openSheet = { el, onKey };
  field.focus();
  return el;
}

export const MoonfallExchangeShare = { openListingShare, closeListingShare, buildListingShareUrl };
